import React, {useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Button} from "antd";
import {LeftOutlined, UserAddOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import {useAppDispatch} from '../../../hooks/useStore';

import PageTitle from "../../../components/page/PageTitle";
import StepsWrapper from "../../../components/StepsWrapper";
import AccountPersonAddInformation from "../../../components/account/AccountPersonAddInformation";
import AccountPersonAddReview from "../../../components/account/AccountPersonAddReview";
import {addExistingPersonToAccount, addNonExistingPersonToAccount} from "../../../helpers/account";
import {messageType, showMessages} from "../../../helpers/message";
import {getActiveAccount} from "../../../lib/activeMember";

import "./AccountPersonAdd.less";
import {apiSlice} from "../../../store/api/apiSlice";

const AccountPersonAdd = () => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // Prevent the URL from being accessible if staff have not selected an account.
    const accountID = getActiveAccount() ? getActiveAccount().id : navigate('/member_change');

    // Loading and stepper states.
    const [loading, setLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);

    // Disclaimer check box to prevent submission on review component.
    const [checked, setChecked] = useState(false);
    const [showCheckBoxError, setShowCheckBoxError] = useState(false);

    // Determine if the person being added is pre-existing or new.
    const [existingPerson, setExistingPerson] = useState(true);

    // Individual IDs for a person selected on the search table, once a radio button is filled.
    const [selectedPersonID, setSelectedPersonID] = useState(null);
    const [selectedProfileID, setSelectedProfileID] = useState(null);

    // The review page needs access to the person's information.
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [birthDate, setBirthDate] = useState(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // The SearchPerson and PersonalInformationForm components needs access to set these states and setters.
    // Bundle them in an object to pass in.
    const personStates = {
        person_id: selectedPersonID,
        set_person_id: setSelectedPersonID,
        set_profile_id: setSelectedProfileID,
        first_name: firstName,
        set_first_name: setFirstName,
        last_name: lastName,
        set_last_name: setLastName,
        birth_date: birthDate,
        set_birth_date: setBirthDate,
        user_email: email,
        set_user_email: setEmail,
        password: password,
        set_password: setPassword,
        confirm_password: confirmPassword,
        set_confirm_password: setConfirmPassword,
    };

    /**
     * The parent form submission API req for the StepsWrapper. Either calls addExistingPersonToAccount.
     * Or calls addNonExistingPersonToAccount.
     *
     * Both subcomponents found in AccountPersonAddInformation.
     *
     * Existing person found using SearchPerson - addExistingPersonToAccount.
     * Non-existing person filled out using PersonalInformationForm - addNonExistingPersonToAccount.
     *
     */
    const handleSubmitFinish = async () => {

        // Prevent submission if the user has not agreed to the disclaimer checkbox.
        if (!checked) {
            setShowCheckBoxError(true);
        } else {
            setShowCheckBoxError(false);
            setLoading(true);

            let personID = selectedPersonID;
            let profileID = selectedProfileID;
            const formattedBirthDate = birthDate ? dayjs(birthDate).format('YYYY-MM-DD') : null;

            // Conditional path for a non-existing person.
            if (existingPerson === false) {
                try {
                    const abortSignal = abortSignalRef.current;
                    await addNonExistingPersonToAccount(
                        {accountID, firstName, lastName, birthDate: formattedBirthDate, email, password}, abortSignal
                    );
                    // Temporarily using dispatch to invalidate cache for 'Profile' and 'AccountProfile' tag.
                    // This will be replaced with RTK Query refactor to handle cache invalidation directly.
                    dispatch(apiSlice.util.invalidateTags(['Profile', 'AccountProfile']));

                    setLoading(false);
                    showMessages('Person successfully added to account. Verification email has been sent to the member.', messageType.success);
                    navigate('/account/account_info');
                } catch (error) {
                    showMessages(error.message, messageType.error);
                    setLoading(false);
                }
            }
            // Conditional path for an existing person.
            else {
                try {
                    const abortSignal = abortSignalRef.current;
                    await addExistingPersonToAccount(
                        {accountID, personID, profileID}, abortSignal
                    );
                    // Temporarily using dispatch to invalidate cache for 'Profile' and 'AccountProfile' tag.
                    // This will be replaced with RTK Query refactor to handle cache invalidation directly.
                    dispatch(apiSlice.util.invalidateTags(['Profile', 'AccountProfile']));

                    setLoading(false);
                    showMessages('Person successfully added to account.', messageType.success);
                    navigate('/account/account_info');
                } catch (error) {
                    showMessages(error.message, messageType.error);
                    setLoading(false);
                }
            }
        }
    }

    // Handlers for the next and back buttons of the StepsWrapper.
    const goNext = () => {
        setCurrentStep(currentStep + 1);
    }

    const goBack = (e) => {
        e.preventDefault();
        setCurrentStep(currentStep - 1);
        setChecked(false);
        setShowCheckBoxError(false);

        // Depending on if a user clicks the back button after searching or filling clear the fields.
        if (existingPerson === true) {
            setSelectedPersonID(null);
            setFirstName('');
            setLastName('');
            setBirthDate(null);
            setEmail('');
            setPassword('');
            setConfirmPassword('');
        }
    }

    const steps = [
        {
            title: 'Fill in information',
            content: <AccountPersonAddInformation
                personStates={personStates}
                handleFinish={goNext}
                existingPerson={existingPerson}
                setExistingPerson={setExistingPerson}

            />,
            form: 'account-person-add-information'
        },
        {
            title: 'Review',
            content: <AccountPersonAddReview
                firstName={firstName}
                lastName={lastName}
                email={email}
                birthDate={birthDate}
                checked={checked}
                setChecked={setChecked}
                showCheckBoxError={showCheckBoxError}
            />
        }
    ]

    return (
        <>
            <div className="account-person-add-header">
                <Button icon={<LeftOutlined/>} onClick={() => navigate(-1)}/>
                <PageTitle title="Add Person to Account" icon={<UserAddOutlined/>}/>
            </div>
            <StepsWrapper
                currentStep={currentStep}
                steps={steps}
                goBack={goBack}
                isLoading={loading}
                submitText="Add Person to Account"
                handleFinish={handleSubmitFinish}
            />
        </>
    );
};

AccountPersonAdd.propTypes = {};

export default AccountPersonAdd;
